<script lang="ts" setup>
import { DialogMessage } from '@/components/dialogs';
import { AppButton, AppLogo, AppTextField, AppTypography } from '@/core/components';
import useAccountStore from '@/store/account';
import type { CommonErrorResponse } from '@/types/ErrorResponse';
import { PHONE_NUMBER_MASK } from '@/utils/enums/PhoneNumbers';
import { storeToRefs } from 'pinia';
import type { LoginForm } from '../types';

const { VALIDATIONS, PASSWORD_VALIDATION } = useValidation();
const { form, handleSubmit, getErrors } = useForm<LoginForm>({
  form: {
    mobile: '61',
  },
  rules: {
    mobile: {
      required: VALIDATIONS.required('Mobile'),
    },
  },
});

const router = useRouter();

const {
  pending: verifyLoading,
  execute: execSendVerify,
  error: verifyError,
} = useSubmit<{}, { data: CommonErrorResponse }>();

const loginError = ref({ data: { message: '' } });

const showDialog = ref(false);

const { data, pending: loginLoading, execute: execSubmit, error: submitError } = useSubmit();

const accountStore = useAccountStore();
const { phoneNumber: phoneNumberStore, onboarded: onboardStore } = storeToRefs(accountStore);

async function handleSignIn(): Promise<void> {
  handleSubmit(async function () {
    try {
      loginLoading.value = true;
      const accountPhone = `+${form.value.mobile.replaceAll(' ', '')}`;
      await execSubmit('auth/check-username', {
        method: 'POST',
        body: {
          username: accountPhone,
        },
      });

      if (submitError.value?.data?.error_code === 'ACCOUNT_DISABLED') {
        showDialog.value = true;
      }

      if (data.value) {
        await nextTick();
        await execSendVerify('auth/otp/generate', {
          method: 'POST',
          body: { phone_number: accountPhone },
        });

        if (!verifyError.value) {
          phoneNumberStore.value = data.value.data.username;
          onboardStore.value = data.value.data.is_onboarded;

          router.push({ name: 'verify' });
        }
      }
    } catch (error) {
      loginError.value.data = error.data;
    } finally {
      loginLoading.value = false;
    }
  });
}

const errors = computed(() => {
  return getErrors({ mobile: submitError.value?.data?.message });
});

const options = { mask: PHONE_NUMBER_MASK };
</script>

<template>
  <v-form @submit.prevent="handleSignIn">
    <ClientOnly>
      <v-card flat class="login-container border-radius-3xl">
        <div class="d-flex flex-column align-center">
          <AppLogo width="80" height="80" />
          <AppTypography element="h1" type="large" class="font-title-1">Log in</AppTypography>
          <AppTypography element="p" class="font-body-regular">Enter a mobile number to continue</AppTypography>
        </div>

        <div class="form-container">
          <AppTextField
            v-model="form.mobile"
            :error-messages="errors.mobile"
            label="Mobile number"
            :readonly="loginLoading"
            data-testid="mobile"
            autofocus
            prefix="+"
            data-cy="mobile"
            v-maska="options"
            class="mobile-field"
          >
          </AppTextField>

          <AppButton
            block
            :disabled="!form.mobile"
            data-cy="submit"
            data-testid="submit"
            :loading="loginLoading || verifyLoading"
          >
            Continue
          </AppButton>
        </div>
      </v-card>
      <DialogMessage
        v-model="showDialog"
        :width="400"
        title="Your account is currently disabled"
        message="Please reach out to your Club Manager for assistance."
        @close="showDialog = false"
      />
    </ClientOnly>
  </v-form>
</template>

<style lang="scss" scoped>
.form-container {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.login-container {
  height: 416px;

  margin: auto !important;
  margin-bottom: 60px !important;
  background-color: rgb(var(--v-theme-surface-background)) !important;
  padding: 40px 24px;

  h1 {
    margin-top: 22px;
  }

  p {
    margin-top: 12px;
  }
}

.mobile-field {
  margin-bottom: 0 !important;

  :deep(.v-input__control) {
    margin-bottom: 0 !important;
  }

  :deep(.v-input__details) {
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    min-height: 0 !important;
  }

  :deep(.v-messages) {
    margin-bottom: 0 !important;
    min-height: 0 !important;
  }
}

@media (min-width: 768px) {
  .login-container {
    width: 500px;
    padding: 40px;
  }
}
</style>
